import { useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog } from '@/components/ui';
import OfferHero from './OfferHero';
import { OfferTicketList } from './OfferTicket';
import { openOffers } from './campaignOfferCopy';
import { useOfferNow } from './offerMotion';
import { useCampaignBonusOffers, useOpenAddMoney } from './CampaignBonusOffersContext';
import { useShopAnnouncementOpen } from './shopAnnouncementOpen';

/** First-open ad for the current set of campaigns. Don't show this again hides that set only. */
export default function CampaignBonusPopup() {
  const { t } = useTranslation();
  const { offers, popupDismissed, dismissPopup } = useCampaignBonusOffers();
  const openAddMoney = useOpenAddMoney();
  const shopAnnouncementOpen = useShopAnnouncementOpen();
  const now = useOfferNow();
  const [sessionClosed, setSessionClosed] = useState(false);
  const [saving, setSaving] = useState(false);
  // Focus starts on the main action, not on "Don't show this again".
  const addMoneyRef = useRef<HTMLButtonElement>(null);

  // Nothing left to claim (only used slots): no ad.
  const open = openOffers(offers);
  if (open.length === 0 || popupDismissed || sessionClosed || shopAnnouncementOpen) return null;
  const [hero, ...more] = open;

  const goAddFunds = () => {
    setSessionClosed(true);
    openAddMoney();
  };

  const hideForever = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await dismissPopup();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={() => {}}
      dismissible={false}
      title={t('campaignBonus.popupTitle', { count: open.length, defaultValue: 'Deposit bonuses for you' })}
      size="md"
      placement="center"
      initialFocus={addMoneyRef}
      footer={
        <>
          <Button variant="ghost" size="lg" onClick={() => void hideForever()} disabled={saving}>
            {t('campaignBonus.dontShowAgain', "Don't show this again")}
          </Button>
          <Button ref={addMoneyRef} variant="primary" size="lg" emphasis icon={<Plus strokeWidth={2.5} />} onClick={goAddFunds} disabled={saving}>
            {t('header.addMoney', 'Add money')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-testid="campaign-bonus-popup">
        <OfferHero offer={hero} now={now} />
        {more.length > 0 ? (
          <section className="flex flex-col gap-2" aria-label={t('campaignBonus.moreOffers', 'More bonuses')}>
            <h3 className="m-0 font-ui text-small font-semibold text-secondary">{t('campaignBonus.moreOffers', 'More bonuses')}</h3>
            <OfferTicketList offers={more} now={now} />
          </section>
        ) : null}
      </div>
    </Dialog>
  );
}
